import { computed } from 'vue';
import type { Id } from '@vp/shared-types';
import { boxBounds, componentOf, componentRoot, createComponent, detachInstance, instantiateComponent, listComponents, masterOf, resetInstance } from '@vp/core';
import { live, ui } from './bridge';
import { flyTo } from './bookmarks';
import { askText } from '../dialogs';
import { t } from '../i18n';

/**
 * Components in the app: make one from the selection, insert instances, go to the master, reset or
 * detach an instance. The sync itself lives in the core (ComponentSync, one undo step with the change).
 */

/** Masters on the open canvas. */
export const components = computed(() => {
  void ui.docRev;
  const ed = live.editor;
  return ed ? listComponents(ed.doc) : [];
});

/** The component the single selected object is (or belongs to). */
export const selectedComponent = computed(() => {
  void ui.docRev;
  const ed = live.editor;
  if (!ed || ui.selection.length !== 1) return null;
  const root = componentRoot(ed.doc, ui.selection[0]);
  const c = componentOf(root);
  return root && c ? { root, ...c, name: c.name ?? (c.role === 'instance' ? masterOf(ed.doc, c.id)?.component?.name : undefined) ?? '' } : null;
});

export async function makeComponent(): Promise<void> {
  const ed = live.editor;
  if (!ed || ui.readOnly || !ui.selection.length) return;
  const first = ed.doc.get(ui.selection[0]);
  const name = await askText(t('components.namePrompt'), first?.name || t('components.defaultName', { n: components.value.length + 1 }));
  if (name === null) return;
  const id = ed.edit('component.create', (tx) => createComponent(tx, ed.selection, name));
  if (id) ed.select([id]);
}

/** An instance beside the master (or at the view centre when the master is far away). */
export function insertInstance(componentId: string, at?: { x: number; y: number }): Id | null {
  const ed = live.editor;
  if (!ed || ui.readOnly) return null;
  const master = masterOf(ed.doc, componentId);
  if (!master) return null;
  const b = boxBounds(master);
  let pos = at;
  if (!pos) {
    const c = ed.pointerWorldOrCenter();
    const view = live.renderer?.camera.visibleRect();
    const beside = { x: b.x + b.w + 40, y: b.y };
    const inView = view && beside.x >= view.x && beside.y >= view.y && beside.x + b.w <= view.x + view.w && beside.y + b.h <= view.y + view.h;
    pos = inView ? beside : { x: c.x - b.w / 2, y: c.y - b.h / 2 };
  }
  const id = ed.edit('component.instance', (tx) => instantiateComponent(tx, componentId, pos!));
  if (id) ed.select([id]);
  return id ?? null;
}

export function goToMaster(componentId: string): void {
  const ed = live.editor;
  const m = ed && masterOf(ed.doc, componentId);
  const cam = live.renderer?.camera;
  if (!ed || !m || !cam) return;
  ed.select([m.id]);
  const b = boxBounds(m);
  flyTo({ cx: b.x + b.w / 2, cy: b.y + b.h / 2, zoom: Math.min(Math.max(cam.zoom, 0.5), cam.width / (b.w + 200), cam.height / (b.h + 200)) });
}

export function detach(rootId: Id): void {
  live.editor?.edit('component.detach', (tx) => detachInstance(tx, rootId));
}

export function resetOverrides(rootId: Id): void {
  live.editor?.edit('component.reset', (tx) => resetInstance(tx, rootId));
}

export function renameComponent(rootId: Id, name: string): void {
  const ed = live.editor;
  const o = ed?.doc.get(rootId);
  const c = componentOf(o);
  if (!ed || !o || c?.role !== 'master' || !name.trim()) return;
  ed.updateObjects([{ id: rootId, patch: { component: { ...c, name: name.trim().slice(0, 80) } } as never }], 'component.rename');
}
